/**
 * 收件人通讯录里的两件纯逻辑:**怎么算同一个人** 和 **错误标记有哪几种**。
 *
 * 放在 shared 里是因为前后端都要用:后端建单时靠它去重,
 * 前端列表里靠它显示标记的含义。
 */

/**
 * 坐标截断到小数点后两位。
 *
 * ★ 两位小数大约是 1 公里。再精细一点(比如四位,~10 米)的话,
 *   同一个门牌因为编码服务给的坐标差了几米就会被当成两个人 ——
 *   通讯录里于是躺着五条一模一样的记录,客服不知道该点哪一条。
 *   再粗一点(一位,~10 公里)则会把一整个街区的人合成一个。
 */
export function truncCoord(v: number | string | null | undefined): string {
  if (v == null || v === '') return '0';
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  // 朝零截断,不是四舍五入 —— 只要求稳定,不要求最近
  const s = n.toFixed(6);
  const dot = s.indexOf('.');
  return dot < 0 ? s : s.slice(0, dot + 3);
}

/**
 * 去重键:电话 + 粗坐标。
 *
 * ★ 只按电话去重是不够的。同一个人白天在公司、晚上在家,
 *   两个地址都真实存在,合成一条就一定会送错一半。
 * ★ 只按地址也不行:一栋楼里几十个人。
 *
 * ★ **没有坐标时用地址,不是用 0**。
 *   都记成 `电话|0|0` 的话,同一个号码下的两个不同的人会被合成一条 ——
 *   后来的那一单把先前那条的下单次数加一,而名字和地址还是旧的:
 *   看起来像「这个人又下单了」,其实是另一个人。
 *   公司前台的号码、家里的座机、以及录错的号码,都会撞上这个。
 *   (这条是真踩出来的:一张新单并进了一条完全不相干的旧记录。)
 */
export function contactDedupKey(
  phone: string,
  lat: number | string | null | undefined,
  lng: number | string | null | undefined,
  address?: string | null,
): string {
  /*
   * ★ 键里**不带名字**,这是有意的。
   *
   *   同一个人的名字写法本来就会变(「王先生」→「王小明」),
   *   带上的话每换一种写法就多一条记录 —— 而那正是这个键要防住的事。
   *
   *   「同一个地址有两个收货人」不需要靠名字区分:他们的**电话不一样**,
   *   而电话在键里 —— 所以那种情况本来就不会撞。
   *   真撞上了(电话和地址都一样),那就是同一个人的两种写法,
   *   该问的是「用哪个名字」,不是「要不要再存一条」。
   */
  const hasCoord =
    lat != null &&
    lng != null &&
    Number.isFinite(Number(lat)) &&
    Number.isFinite(Number(lng)) &&
    !(Number(lat) === 0 && Number(lng) === 0);
  if (!hasCoord) return `${phone}|addr:${addressToken(address)}`;
  return `${phone}|${truncCoord(lat)}|${truncCoord(lng)}`;
}

/** 地址压成一个稳定的短串:去掉大小写、空格、标点 —— 它们不改变「是不是同一个地方」 */
export function addressToken(a: string | null | undefined): string {
  const t = (a ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '')
    .slice(0, 48);
  return t || '0';
}

/** 电话比对用:去掉空格、横杠、括号,只留数字和开头的 + */
export function phoneDigits(s: string): string {
  return s.replace(/[^\d+]/g, '');
}

export const CONTACT_ERROR_TAGS = {
  /*
   * ★ 这三项说的是「**这条记录本身是错的**」,不是「这一趟没成」。
   *
   *   原来司机端那边把它叫「电话打不通」—— 而打不通和号码错是两回事:
   *   对方在忙、在开会、手机静音,都会打不通,号码却好好的。
   *   叫「打不通」的后果是司机一次没打通就顺手标上,
   *   而这个标记会跟着这个人**以后每一单**:客服看到会先打电话确认,
   *   甚至不敢发单。一次没接听换来长期的不敢发货,这笔账不划算。
   *
   *   「这一趟为什么没送到」有专门的地方记(投递失败原因,
   *   那里才有「电话打不通」这一项)——两件事不该共用一个标记。
   */
  PHONE: {
    label: '电话号码不对',
    description: '空号、停机,或者本人说不是他的号码',
    whenSeen: '建单前先跟商家确认号码,否则司机到了门口联系不上人',
    /** 什么情况**不该**标 —— 标错的代价落在这个人以后的每一单上 */
    notThis: '只是这次没打通(占线、没接)不算 —— 那是投递失败的原因,不是号码错',
  },
  ADDRESS: {
    label: '地址不对',
    description: '门牌对不上、根本没有这个地址,或者本人已经搬走',
    whenSeen: '照这个地址派下去,司机大概率白跑一趟',
    notThis: '一时找不到、导航绕了路不算 —— 地址本身是对的',
  },
  BOTH: {
    label: '电话和地址都不对',
    description: '两样都核不上',
    whenSeen: '这条基本等于作废,重新问一遍再用',
    notThis: '',
  },
} as const;

export type ContactErrorTag = keyof typeof CONTACT_ERROR_TAGS;

export const CONTACT_ERROR_TAG_KEYS = Object.keys(CONTACT_ERROR_TAGS) as ContactErrorTag[];

export function isContactErrorTag(v: unknown): v is ContactErrorTag {
  return typeof v === 'string' && v in CONTACT_ERROR_TAGS;
}

export function contactErrorLabel(tag: string | null | undefined): string | null {
  return isContactErrorTag(tag) ? CONTACT_ERROR_TAGS[tag].label : null;
}
